import type { TMessage } from "@simplrsupport/headless-chat-web";
import { useMessagesStore } from "@simplrsupport/headless-chat-web";
import { useReactAnalytics } from "@soluto-private/eventualize-react";
import { useEffect, useMemo } from "react";
import type { GuideSelectionMessage, GuideTextMessage, UiMetadata } from "../logic-builder";
import { isUiMetadataMessage, isValidGuideMessage } from "../logic-builder";
import { getGuideAnalyticDataFromMessage, isGuideSelectionMessage } from "../logic-builder/logicBuilderResolvers";
import { useGuideStore } from "../store";

interface ParseMessagesProps {
    employeeId?: string;
}

interface ParseMessagesResult {
    latestMessage: TMessage | undefined;
    messages: TMessage[];
}

export function useParseMessages({ employeeId }: ParseMessagesProps): ParseMessagesResult {
    const { setAnalyticsInfo, setAvailableGuides, setCurrentGuideName, setUiMetadata } = useGuideStore();
    const { dispatcher } = useReactAnalytics();

    // message store contains all history from the logic builder, including:
    // - messages from the bot (content (current step) + options for a guide step)
    // - messages from the expert (selected option from the guide step)
    const messages = useMessagesStore((s) => s.messages);

    /*
       latestValidMessage is the last message from the bot,
       which correlates to the most recent step in the guide
       if we have no valid step to display, we return no guide
    */
    const latestValidMessage = messages.findLast(isValidGuideMessage);

    // TODO: accept uiMetadata with a CUSTOM message type
    // [EWS-1094](https://solutonashville.atlassian.net/browse/EWS-1094)
    const latestUiMessage = useMemo(() => {
        return messages.findLast((m) => {
            return isUiMetadataMessage(m);
        }) as GuideTextMessage | undefined;
    }, [messages]);
    useEffect(() => {
        const customMetadata: UiMetadata | undefined = latestUiMessage?.data?.customData;
        setUiMetadata(customMetadata);
        setCurrentGuideName({ dispatcher, currentGuideName: customMetadata?.flowName });
        // oxlint-disable-next-line exhaustive-deps
    }, [latestUiMessage?.data?.customData?.flowName]);

    // Set available guides on every guide selection message
    const latestGuideSelectionMessage = useMemo(() => {
        return messages.findLast((m) => {
            return isGuideSelectionMessage(m);
        }) as GuideSelectionMessage | undefined;
    }, [messages]);
    useEffect(() => {
        const availableGuides = latestGuideSelectionMessage?.data?.guideSelection;
        const filteredGuides = availableGuides?.filter((guide) => {
            const { employeeIds } = guide;
            const isEmployeeIdValid = !employeeIds || (employeeId && employeeIds.includes(employeeId));
            return isEmployeeIdValid;
        });
        if (filteredGuides) {
            setAvailableGuides(filteredGuides);
        }
    }, [employeeId, latestGuideSelectionMessage, setAvailableGuides]);

    // Update analytics info on every message update
    const analyticsInfo = useMemo(() => {
        return getGuideAnalyticDataFromMessage(latestValidMessage, messages);
    }, [latestValidMessage, messages]);
    useEffect(() => {
        setAnalyticsInfo(analyticsInfo);
    }, [analyticsInfo, setAnalyticsInfo]);

    return {
        latestMessage: latestValidMessage,
        messages,
    };
}
